import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAcceptJs } from 'react-acceptjs';
import { useSnackbar } from 'notistack';
import useAxiosPrivate from '../../Hooks/useAxiosPrivate';
import { useUser } from '../../Hooks/useUser';

const MakePayment = () => {
  const params = useParams();
  const { attendeeId: legacyAttendeeId, eventId, userId } = params;
  const navigate = useNavigate();
  const location = useLocation();
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const session = useUser();
  const [paymentType, setPaymentType] = useState('full'); // 'full' | 'deposit'

  const fromRegistration = location.state?.fromRegistration || false;
  const regState = location.state || {};
  
  // Determine if this is the new check-in payment flow (2 params) or legacy (1 param)
  const isCheckinPayment = !legacyAttendeeId && eventId && userId;
  const [resolvedAttendeeId, setResolvedAttendeeId] = useState(legacyAttendeeId || null);
  
  const authData = useMemo(
    () => ({
      apiLoginID: process.env.REACT_APP_apiLoginID || '',
      clientKey: process.env.REACT_APP_clientKey || '',
    }),
    []
  );
  const { dispatchData, loading: authNetLoading, error: authNetError } = useAcceptJs({ 
    environment: 'PRODUCTION',
    authData 
  });

  // Payment info state
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Form state
  const [amount, setAmount] = useState('');
  const [billing, setBilling] = useState({
    firstName: '',
    lastName: '',
    address: '',
    apt: '',
    city: '',
    state: 'NC',
    zip: ''
  });
  const [cardData, setCardData] = useState({
    cardNumber: '',
    nameOnCard: '',
    expMonth: '',
    expYear: '',
    cardCode: ''
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});
  const [expDisplay, setExpDisplay] = useState('');

  const depositAllowed = Boolean(paymentInfo?.allowDeposit) &&
  Number(paymentInfo?.depositAmount) > 0 &&
  Number(paymentInfo?.balance) > 0 && 
  !paymentInfo?.order?.DepositPaid;

const depositAmount = Number(paymentInfo?.depositAmount ?? 0);
const depositDueDays = paymentInfo?.depositDueDays ?? null;

  // Resolve attendeeId from eventId+userId for check-in payment flow
  useEffect(() => {
    const resolveAttendeeId = async () => {
      if (isCheckinPayment) {
        try {
          const response = await axiosPrivate.get(`/api/attendees/event/${eventId}/user/${userId}`);
          setResolvedAttendeeId(response.data.attendee.BPAttendeeID);
        } catch (err) {
          console.error('Error resolving attendee:', err);
          setError('Failed to resolve attendee information');
          setIsLoading(false);
        }
      }
    };
    
    if (isCheckinPayment && !resolvedAttendeeId) {
      resolveAttendeeId();
    }
  }, [isCheckinPayment, eventId, userId, resolvedAttendeeId, axiosPrivate]);

  // Fetch payment info on mount
  useEffect(() => {
    const fetchPaymentInfo = async () => {
      try {
        setIsLoading(true);
        const response = await axiosPrivate.get(`/api/attendees/${resolvedAttendeeId}/payment-info`);
        setPaymentInfo(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching payment info:', err);
        setError(err?.response?.data?.error || 'Failed to load payment information');
        enqueueSnackbar(err?.response?.data?.error || 'Failed to load payment information', {
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (resolvedAttendeeId) {
      fetchPaymentInfo();
    }
  }, [resolvedAttendeeId, axiosPrivate, enqueueSnackbar]);

  // Pre-fill billing from View Order modal, order id in navigation state, or profile
  useEffect(() => {
    if (!paymentInfo || !resolvedAttendeeId) return;

    const state = location.state || {};
    const bp = state.billingPrefill;
    if (bp) {
      setBilling((prev) => ({
        ...prev,
        firstName: bp.firstName || prev.firstName,
        lastName: bp.lastName || prev.lastName,
        address: bp.address || prev.address,
        apt: bp.apt || prev.apt,
        city: bp.city || prev.city,
        state: bp.state || prev.state,
        zip: bp.zip || prev.zip,
      }));
    }
    if (state.nameOnCardPrefill) {
      setCardData((prev) => ({
        ...prev,
        nameOnCard: state.nameOnCardPrefill || prev.nameOnCard,
      }));
    }
    if (state.suggestedAmount != null && Number(state.suggestedAmount) > 0) {
      setAmount(String(Number(state.suggestedAmount).toFixed(2)));
    }

    let cancelled = false;
    if (state.orderId && !bp) {
      axiosPrivate
        .get(`/api/orders/${state.orderId}`)
        .then((r) => {
          if (cancelled) return;
          const c = r.data?.customer;
          if (c) {
            setBilling((prev) => ({
              ...prev,
              firstName: c.firstName || prev.firstName,
              lastName: c.lastName || prev.lastName,
              address: c.address || prev.address,
              apt: c.apt || prev.apt,
              city: c.city || prev.city,
              state: c.state || prev.state,
              zip: c.zip || prev.zip,
            }));
            const full = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
            if (full) {
              setCardData((prev) => ({ ...prev, nameOnCard: prev.nameOnCard || full }));
            }
          }
          const rem = r.data?.summary?.remainingBalance;
          if (
            rem != null &&
            Number(rem) > 0 &&
            !(state.suggestedAmount != null && Number(state.suggestedAmount) > 0)
          ) {
            setAmount(String(Number(rem).toFixed(2)));
          }
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [paymentInfo, resolvedAttendeeId, axiosPrivate, location.state]);

  useEffect(() => {
    if (!paymentInfo) return;
    const uid = session?.user?.UserID;
    if (!uid) return;

    const firstName = session?.user?.FirstName ||
      (session?.user?.FullName ? session.user.FullName.split(' ')[0] : '') || '';
    const lastName = session?.user?.LastName ||
      (session?.user?.FullName ? session.user.FullName.split(' ').slice(1).join(' ') : '') || '';

    let cancelled = false;
    axiosPrivate
      .get(`/api/users/usersettings/${uid}`)
      .then((resp) => {
        if (cancelled) return;
        const s = resp.data;
        setBilling((prev) => ({
          ...prev,
          firstName: prev.firstName || firstName,
          lastName: prev.lastName || lastName,
          ...(s && {
            address: prev.address || s.BP_G_Address || '',
            apt: prev.apt || s.BP_G_AddressLine2 || '',
            city: prev.city || s.BP_G_AddressCity || '',
            state: (prev.state && prev.state !== 'NC') ? prev.state : (s.BP_G_AddressState || 'NC'),
            zip: prev.zip || s.BP_G_AddressZip || '',
          }),
        }));
        setCardData((prev) => {
          if (prev.nameOnCard) return prev;
          const full = `${firstName} ${lastName}`.trim();
          return full ? { ...prev, nameOnCard: full } : prev;
        });
      })
      .catch(() => {
        // Still fill name from JWT even if settings fetch fails
        setBilling((prev) => ({
          ...prev,
          firstName: prev.firstName || firstName,
          lastName: prev.lastName || lastName,
        }));
        setCardData((prev) => {
          if (prev.nameOnCard) return prev;
          const full = `${firstName} ${lastName}`.trim();
          return full ? { ...prev, nameOnCard: full } : prev;
        });
      });

    return () => {
      cancelled = true;
    };
  }, [paymentInfo, session?.user?.UserID, session?.user?.FirstName, session?.user?.LastName, axiosPrivate]);

  // Sync amount field when paymentType or paymentInfo changes
useEffect(() => {
  if (!paymentInfo) return;
  if (paymentType === 'deposit' && depositAllowed) {
    setAmount(depositAmount.toFixed(2));
  } else {
    setAmount(Number(paymentInfo.balance).toFixed(2));
  }
}, [paymentType, paymentInfo, depositAllowed, depositAmount]);

  // Validation
  const validateForm = () => {
    const errors = {};

    // Amount validation
    const amountNum = parseFloat(amount);
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      errors.amount = 'Please enter a valid amount';
    } else if (amountNum > paymentInfo?.balance) {
      errors.amount = 'Amount cannot exceed balance';
    }

    //Deposit Validation
    if (paymentType === 'deposit' && depositAllowed && amountNum < depositAmount) {
      errors.amount = `Minimum deposit amount is $${depositAmount.toFixed(2)}`;
    }

    // Billing validation
    if (!billing.firstName.trim()) errors.firstName = 'First name is required';
    if (!billing.lastName.trim()) errors.lastName = 'Last name is required';
    if (!billing.address.trim()) errors.address = 'Address is required';
    if (!billing.city.trim()) errors.city = 'City is required';
    if (!billing.state) errors.state = 'State is required';
    if (!billing.zip.trim()) errors.zip = 'ZIP code is required';
    else if (!/^\d{5}(-\d{4})?$/.test(billing.zip.trim())) {
      errors.zip = 'Invalid ZIP code format';
    }

    // Credit card validation
    if (!cardData.cardNumber.trim()) {
      errors.cardNumber = 'Card number is required';
    } else if (!/^\d{13,19}$/.test(cardData.cardNumber.replace(/\s/g, ''))) {
      errors.cardNumber = 'Invalid card number';
    }

    if (!cardData.nameOnCard.trim()) errors.nameOnCard = 'Name on card is required';

    if (!cardData.expMonth || !cardData.expYear) {
      errors.expiration = 'Expiration date is required';
    } else {
      const month = parseInt(cardData.expMonth);
      const year = parseInt(cardData.expYear);
      if (month < 1 || month > 12) errors.expiration = 'Invalid month';
      if (year < 0 || year > 99) errors.expiration = 'Invalid year';
    }

    if (!cardData.cardCode.trim()) {
      errors.cardCode = 'Security code is required';
    } else if (!/^\d{3,4}$/.test(cardData.cardCode)) {
      errors.cardCode = 'Invalid security code';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!authData.apiLoginID || !authData.clientKey) {
      enqueueSnackbar('Payment is not configured (missing Authorize.net keys).', { variant: 'error' });
      return;
    }

    if (!validateForm()) {
      enqueueSnackbar('Please fix form errors', { variant: 'error' });
      return;
    }

    setIsProcessing(true);

    try {
      // Step 1: Tokenize card data with Authorize.net
      const response = await dispatchData({ cardData: {
        cardNumber: cardData.cardNumber.replace(/\s/g, ''),
        month: cardData.expMonth.padStart(2, '0'),
        year: cardData.expYear.padStart(2, '0'),
        cardCode: cardData.cardCode
      } });

      //console.log('Authorize.net response:', response);

      if (response.messages.resultCode === 'Error') {
        let errorMessage = 'Payment failed';
        if (response.messages.message && response.messages.message.length > 0) {
          errorMessage = response.messages.message[0].text;
        }
        enqueueSnackbar(errorMessage, { variant: 'error' });
        setIsProcessing(false);
        return;
      }

      // Step 2: Send payment to backend
      const opaqueData = response?.opaqueData;
      
      // Get card last four digits and detect card type
      const cardNum = cardData.cardNumber.replace(/\s/g, '');
      const cardLastFour = cardNum.slice(-4);
      let cardType = 'Unknown';
      if (cardNum.startsWith('4')) cardType = 'Visa';
      else if (cardNum.startsWith('5') || cardNum.startsWith('2')) cardType = 'Mastercard';
      else if (cardNum.startsWith('3')) cardType = 'Amex';
      else if (cardNum.startsWith('6')) cardType = 'Discover';

      const paymentResponse = await axiosPrivate.post(
        `/api/attendees/${resolvedAttendeeId}/process-payment`,
        {
          amount: parseFloat(amount),
          // Send full opaqueData (descriptor + value) for Authorize.net createTransactionRequest
          opaqueData,
          billing: {
            ...billing,
            cardLastFour,
            cardType
          }
        }
      );

     // console.log('Payment Response: ',paymentResponse);

      enqueueSnackbar('Payment processed successfully!', { variant: 'success' });
      
      setTimeout(() => {
        if (fromRegistration) {
          navigate('/events/thanks', {
            state: {
              event: regState.event,
              attendee: regState.attendee,
              registration: regState.registration,
              order: regState.order,
              paymentCompleted: true,
            }
          });
        } else {
          navigate('/myreserved');
        }
      }, 1000);

    } catch (err) {
      console.error('Payment error:', err);
      console.error('Payment error messages:', JSON.stringify(err?.messages, null, 2));
      console.error('Auth data used:', { apiLoginID: authData.apiLoginID, clientKey: authData.clientKey ? '***SET***' : '***MISSING***' });
      
      // Check if this is an Authorize.net Accept.js error
      let errorMessage = 'Payment processing failed';
      if (err?.messages?.message && err.messages.message.length > 0) {
        const errDetails = err.messages.message[0];
        errorMessage = `${errDetails.text} (Code: ${errDetails.code})`;
        console.error('Accept.js Error:', errorMessage);
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };


  // Loading state
  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">MAKE PAYMENT</h1>
                <p>Pay on outstanding balances</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12 text-center">
                <p>Loading payment information...</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Error state
  if (error || !paymentInfo) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">MAKE PAYMENT</h1>
                <p>Pay on outstanding balances</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12 text-center">
                <p style={{ color: '#d32f2f' }}>{error || 'Payment information not available'}</p>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Header */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">MAKE PAYMENT</h1>
              <p>Pay on outstanding balances</p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Event Balance Info & Amount */}
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5">
              <div className="col-md-12 mb-3">
                <h3>
                  {(paymentInfo.eventCode && paymentInfo.eventTitle?.startsWith(paymentInfo.eventCode)
                    ? paymentInfo.eventTitle
                    : [paymentInfo.eventCode, paymentInfo.eventTitle].filter(Boolean).join(' ')).trim()} has a balance of{' '}
                  <span className="required-field">${paymentInfo.balance.toFixed(2)}</span>
                </h3>
              </div>
              {/* Payment Type Selection */}
<div className="col-md-12 mb-4">
  {depositAllowed ? (
    <>
      <label className="mb-2 d-block" style={{ fontWeight: 600 }}>
        Payment Option
      </label>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>

        {/* Full Payment Option */}
        <label
          style={{
            flex: 1,
            minWidth: 220,
            border: `2px solid ${paymentType === 'full' ? '#003063' : '#dee2e6'}`,
            borderRadius: 8,
            padding: '14px 18px',
            cursor: 'pointer',
            backgroundColor: paymentType === 'full' ? '#f0f4ff' : '#fff',
            transition: 'all 0.15s',
          }}
        >
          <input
            type="radio"
            name="paymentType"
            value="full"
            checked={paymentType === 'full'}
            onChange={() => setPaymentType('full')}
            style={{ marginRight: 10 }}
          />
          <span style={{ fontWeight: 600 }}>Pay Full Balance</span>
          <div style={{ marginTop: 4, color: '#555', fontSize: '0.875rem' }}>
            ${Number(paymentInfo.balance).toFixed(2)} — clears your balance completely
          </div>
        </label>

        {/* Deposit Option */}
        <label
          style={{
            flex: 1,
            minWidth: 220,
            border: `2px solid ${paymentType === 'deposit' ? '#003063' : '#dee2e6'}`,
            borderRadius: 8,
            padding: '14px 18px',
            cursor: 'pointer',
            backgroundColor: paymentType === 'deposit' ? '#f0f4ff' : '#fff',
            transition: 'all 0.15s',
          }}
        >
          <input
            type="radio"
            name="paymentType"
            value="deposit"
            checked={paymentType === 'deposit'}
            onChange={() => setPaymentType('deposit')}
            style={{ marginRight: 10 }}
          />
          <span style={{ fontWeight: 600 }}>Pay Deposit</span>
          <div style={{ marginTop: 4, color: '#555', fontSize: '0.875rem' }}>
            ${depositAmount.toFixed(2)}
          </div>
        </label>

      </div>
    </>
  ) : (
    <p style={{ color: '#555', fontSize: '0.9rem', marginBottom: 0 }}>
      Full balance payment required for this registration.
    </p>
  )}
</div>

{/* Amount Field */}
<div className="col-md-6 mb-3">
  <label className="mb-2">
    {depositAllowed ? 'Amount to Pay' : 'Enter Amount to Pay'}
  </label>
  <div className="input-group">
    <div className="input-group-prepend">
      <span className="input-group-text" id="inputGroupPrepend">$</span>
    </div>
    <input
      type="number"
      step="0.01"
      className="form-control"
      placeholder="0.00"
      value={amount}
      onChange={(e) => setAmount(e.target.value)}
      required
    />
  </div>
  {validationErrors.amount && (
    <small style={{ color: '#d32f2f' }}>{validationErrors.amount}</small>
  )}
</div>
              <div className="col-md-6"></div>
            </div>
          </div>
        </div>

        <hr />

        {/* Billing Information */}
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5 mb-5">
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">
                    <span className="required-field">*</span>First Name
                  </label>
                  <input
                    type="text"
                    maxLength="200"
                    className="form-control"
                    placeholder="First name"
                    value={billing.firstName}
                    onChange={(e) => setBilling({ ...billing, firstName: e.target.value })}
                  />
                  {validationErrors.firstName && (
                    <small style={{ color: '#d32f2f' }}>{validationErrors.firstName}</small>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">
                    <span className="required-field">*</span>Last Name
                  </label>
                  <input
                    type="text"
                    maxLength="200"
                    className="form-control"
                    placeholder="Last name"
                    value={billing.lastName}
                    onChange={(e) => setBilling({ ...billing, lastName: e.target.value })}
                  />
                  {validationErrors.lastName && (
                    <small style={{ color: '#d32f2f' }}>{validationErrors.lastName}</small>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">
                    <span className="required-field">*</span>Address
                  </label>
                  <input
                    type="text"
                    maxLength="200"
                    className="form-control"
                    placeholder="Address"
                    value={billing.address}
                    onChange={(e) => setBilling({ ...billing, address: e.target.value })}
                  />
                  {validationErrors.address && (
                    <small style={{ color: '#d32f2f' }}>{validationErrors.address}</small>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <input
                    type="text"
                    maxLength="200"
                    className="form-control"
                    placeholder="Apartment, suite, etc. (optional)"
                    value={billing.apt}
                    onChange={(e) => setBilling({ ...billing, apt: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">
                    <span className="required-field">*</span>City
                  </label>
                  <input
                    type="text"
                    maxLength="200"
                    className="form-control"
                    placeholder="City"
                    value={billing.city}
                    onChange={(e) => setBilling({ ...billing, city: e.target.value })}
                  />
                  {validationErrors.city && (
                    <small style={{ color: '#d32f2f' }}>{validationErrors.city}</small>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">State</label>
                  <select
                    className="DropDownField form-control"
                    value={billing.state}
                    onChange={(e) => setBilling({ ...billing, state: e.target.value })}
                  >
                    <option value="">Select State</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="DC">District of Columbia</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </select>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">
                    <span className="required-field">*</span>ZIP Code
                  </label>
                  <input
                    type="text"
                    maxLength="10"
                    className="form-control"
                    placeholder="ZIP Code"
                    value={billing.zip}
                    onChange={(e) => setBilling({ ...billing, zip: e.target.value })}
                  />
                  {validationErrors.zip && (
                    <small style={{ color: '#d32f2f' }}>{validationErrors.zip}</small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr />

        {/* Credit Card Information */}
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row mt-5 mb-0">
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <div className="row align-items-center mb-2">
                    <div className="col-md-6">
                      <label className="required-caption mb-0">
                        <span className="required-field">*</span>Credit Card
                      </label>
                    </div>
                    <div className="col-md-6">
                      <ul id="credit-cards">
                        <li><i className="fa-brands fa-cc-visa" aria-hidden="true"></i></li>
                        <li><i className="fa-brands fa-cc-mastercard" aria-hidden="true"></i></li>
                        <li><i className="fa-brands fa-cc-discover" aria-hidden="true"></i></li>
                        <li><i className="fa-brands fa-cc-amex" aria-hidden="true"></i></li>
                      </ul>
                    </div>
                  </div>
                  <input
                    type="text"
                    maxLength="19"
                    className="form-control"
                    value={cardData.cardNumber}
                    onChange={(e) => setCardData({ ...cardData, cardNumber: e.target.value })}
                  />
                  {validationErrors.cardNumber && (
                    <small style={{ color: '#d32f2f' }}>{validationErrors.cardNumber}</small>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">
                    <span className="required-field">*</span>Name on Card
                  </label>
                  <input
                    type="text"
                    maxLength="200"
                    className="form-control"
                    value={cardData.nameOnCard}
                    onChange={(e) => setCardData({ ...cardData, nameOnCard: e.target.value })}
                  />
                  {validationErrors.nameOnCard && (
                    <small style={{ color: '#d32f2f' }}>{validationErrors.nameOnCard}</small>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">
                    <span className="required-field">*</span>Expiration Date
                  </label>
                  <input
  type="text"
  maxLength="5"
  className="form-control"
  placeholder="MM/YY"
  value={expDisplay}
  onChange={(e) => {
    // Strip everything except digits
    const digits = e.target.value.replace(/\D/g, '');

    // Auto-insert slash after MM
    let formatted = digits;
    if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
    }

    setExpDisplay(formatted);
    setCardData({
      ...cardData,
      expMonth: digits.slice(0, 2),
      expYear:  digits.slice(2, 4),
    });
  }}
/>
                  {validationErrors.expiration && (
                    <small style={{ color: '#d32f2f' }}>{validationErrors.expiration}</small>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group mb-3">
                  <label className="required-caption mb-2">
                    <span className="required-field">*</span>Security Code
                  </label>
                  <input
                    type="text"
                    maxLength="4"
                    className="form-control"
                    value={cardData.cardCode}
                    onChange={(e) => setCardData({ ...cardData, cardCode: e.target.value })}
                  />
                  {validationErrors.cardCode && (
                    <small style={{ color: '#d32f2f' }}>{validationErrors.cardCode}</small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr />

        {/* Submit Button */}
        <div className="full-row white">
          <div className="container-fluid">
            <div className="row my-3">
              <div className="col-md-12 text-center">
                <button
                  type="submit"
                  className="button-default"
                  disabled={isProcessing || authNetLoading}
                >
                  {isProcessing ? 'PROCESSING...' : 'MAKE PAYMENT'}
                </button>
              </div>
            </div>
            {fromRegistration && Boolean(regState?.event?.PayLater) && (
    <div className="row mb-3">
        <div className="col-md-12 text-center">
            <p style={{ color: '#666', margin: '8px 0 12px' }}>
                Not ready to pay now? You can pay later by mail or phone.
            </p>
            <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                    navigate('/events/thanks', {
                        state: {
                            event: regState.event,
                            attendee: regState.attendee,
                            registration: regState.registration,
                            order: regState.order,
                            payLater: true,
                        }
                    });
                }}
            >
                PAY LATER
            </button>
        </div>
    </div>
)}
          </div>
        </div>
      </form>
    </main>
  );
};

export default MakePayment;